import { useRef, type ChangeEvent, type ClipboardEvent, type KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  invalid?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  ariaLabel?: string;
}

/**
 * Segmented one-time-code input: `length` separate single-digit boxes with
 * auto-advance, backspace-to-previous, arrow-key navigation, and full-code
 * paste. Digits only. The value is a plain string of up to `length` digits.
 */
export function OtpInput({
  value,
  onChange,
  length = 6,
  invalid = false,
  disabled = false,
  autoFocus = false,
  ariaLabel = 'Verification code',
}: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = value.replace(/\D/g, '').slice(0, length);

  const focusBox = (i: number) => {
    const el = refs.current[Math.max(0, Math.min(length - 1, i))];
    el?.focus();
    el?.select();
  };

  // Replace/insert starting at index `start`, keeping the result numeric and
  // capped at `length`.
  const writeAt = (start: number, chars: string) => {
    const arr = digits.split('');
    while (arr.length < length) arr.push('');
    const clean = chars.replace(/\D/g, '');
    for (let k = 0; k < clean.length && start + k < length; k++) {
      arr[start + k] = clean[k];
    }
    onChange(arr.join('').replace(/\D/g, '').slice(0, length));
    return Math.min(start + clean.length, length - 1);
  };

  const handleChange = (i: number, e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) return; // clearing is handled in keydown (Backspace)
    // writeAt already returns the box AFTER the last written digit (i+1 for a
    // single keystroke), so focus that directly — adding +1 here skipped a box.
    const nextFocus = writeAt(i, raw);
    focusBox(nextFocus);
  };

  const handleKeyDown = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const arr = digits.split('');
      while (arr.length < length) arr.push('');
      if (arr[i]) {
        arr[i] = '';
        onChange(arr.join('').replace(/\D/g, '').slice(0, length));
      } else if (i > 0) {
        arr[i - 1] = '';
        onChange(arr.join('').replace(/\D/g, '').slice(0, length));
        focusBox(i - 1);
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusBox(i - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusBox(i + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    onChange(pasted);
    focusBox(Math.min(pasted.length, length - 1));
  };

  return (
    <div className="flex items-center gap-2 sm:gap-3">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          aria-label={`${ariaLabel} digit ${i + 1}`}
          value={digits[i] || ''}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          className={cn(
            'h-12 min-w-0 flex-1 rounded-xl border-2 bg-white text-center text-lg font-semibold text-gray-900 outline-none transition-colors sm:h-14',
            // Match the standard input active state (same token as PhoneNumberInput).
            'border-gray-200 caret-input-focus focus:border-input-focus focus:ring-1 focus:ring-input-focus',
            invalid && '!border-red-400 focus:!border-red-500 focus:!ring-red-500',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        />
      ))}
    </div>
  );
}
